import { focusWithoutScroll } from '../../common/focus-without-scroll/focus-without-scroll.js';
import { createViewportObserver } from '../../common/layout-observer/layout-observer.js';
import {
    initVideoVariants,
    readVideoSources,
    videoMp4Fallback,
} from '../../common/video-sources/video-sources.js';
const initializedPlayers = new WeakSet();
const initializedVideos = new WeakSet();

function bindVideo(video, retryPlayback) {
    if (initializedVideos.has(video)) return null;
    initializedVideos.add(video);
    let attached = false;
    let retried = false;
    const load = () => {
        if (attached) return;
        attached = true;
        video.querySelectorAll('source[data-content-video-src]').forEach((source) => {
            source.src = source.dataset.contentVideoSrc;
        });
        video.load();
    };
    video.addEventListener('error', () => {
        const fallback = retried ? '' : videoMp4Fallback(video.currentSrc, readVideoSources(video));
        if (!fallback || !video.isConnected) return;
        retried = true;
        retryPlayback(() => {
            video.src = fallback;
            video.load();
        });
    });
    return load;
}

function bindPlayer(root) {
    if (initializedPlayers.has(root)) return;
    const video = root.querySelector('video');
    const play = root.querySelector('[data-content-video-play]');
    const poster = root.querySelector('[data-content-video-poster]');
    if (!video || !play) return;
    initializedPlayers.add(root);
    const document = root.ownerDocument;
    const view = document.defaultView;
    const autoplay = root.dataset?.contentVideoAutoplay === 'true';
    const showControls = root.dataset?.contentVideoControls !== 'false';
    const showPlay = showControls && !autoplay;
    let observer;
    let removalObserver;
    let visible = false;
    let fresh = false;
    let disposed = false;
    let activated = false;
    let wantsPlayback = autoplay;
    let resumeOnVisibility = false;
    let pending = false;
    let generation = 0;
    let expectedPauses = 0;
    let pendingRetry = null;
    video.controls = false;
    video.muted = Boolean(video.defaultMuted);
    play.hidden = !showPlay;

    const canPlay = () => !disposed && root.isConnected && visible && !document.hidden;
    const invalidatePlayback = () => {
        generation += 1;
        pending = false;
    };
    const pausePlayback = () => {
        resumeOnVisibility ||= wantsPlayback && !video.paused;
        invalidatePlayback();
        if (video.paused) return;
        // Native pause events arrive asynchronously, possibly after a fresh
        // visible snapshot has already resumed this same video.
        expectedPauses += 1;
        video.pause();
    };
    const load = bindVideo(video, (retry) => {
        if (disposed) return;
        pausePlayback();
        pendingRetry = retry;
        start();
    });
    function start(moveFocus = false) {
        if (!canPlay() || !fresh || !wantsPlayback || pending || video.ended) return;
        if (activated && !video.paused && !pendingRetry) return;
        if (activated && !resumeOnVisibility && !moveFocus && !pendingRetry) return;
        if (!activated) {
            activated = true;
            if (poster) video.poster = poster.currentSrc || poster.src;
            load?.();
        }
        if (pendingRetry) {
            const retry = pendingRetry;
            pendingRetry = null;
            retry();
        }
        if (poster) poster.hidden = true;
        video.controls = showControls;
        video.tabIndex = showControls ? 0 : -1;
        play.hidden = true;
        if (moveFocus && showControls) focusWithoutScroll(video);
        const request = ++generation;
        resumeOnVisibility = false;
        pending = true;
        Promise.resolve(video.play())
            .then(() => {
                if (request === generation) pending = false;
                if (!canPlay() || !wantsPlayback) pausePlayback();
            })
            .catch(() => {
                if (disposed || request !== generation || !root.isConnected) return;
                pending = false;
                wantsPlayback = false;
                resumeOnVisibility = false;
                if (poster) poster.hidden = !showPlay && showControls;
                play.hidden = !showPlay;
                if (moveFocus && showPlay) focusWithoutScroll(play);
            });
    }
    const onPlay = () => {
        if (disposed || video.paused) return;
        wantsPlayback = true;
        resumeOnVisibility = false;
        if (!canPlay()) pausePlayback();
    };
    const onPlaying = () => {
        if (!canPlay() || !wantsPlayback) pausePlayback();
    };
    const onPause = () => {
        if (expectedPauses > 0) {
            expectedPauses -= 1;
            return;
        }
        wantsPlayback = false;
        resumeOnVisibility = false;
        invalidatePlayback();
    };
    const onEnded = () => {
        wantsPlayback = false;
        resumeOnVisibility = false;
        invalidatePlayback();
    };
    const observe = () => {
        if (observer || disposed) return;
        observer = createViewportObserver((entries) => {
            const entry = entries.findLast((entry) => entry.target === root);
            return () => {
                if (!root.isConnected) {
                    dispose();
                    return;
                }
                fresh = !entry?.invalidated;
                // Scroll invalidation gates new starts, but existing playback
                // waits for actual bounds so scrolling within the frame is smooth.
                if (!fresh && !document.hidden) return;
                visible = Boolean(entry?.visible);
                if (canPlay()) start();
                else pausePlayback();
            };
        }, view);
        observer.observe(root);
    };
    const observeAfterLoad = () => {
        if (!root.isConnected) dispose();
        else observe();
    };
    const onClick = (event) => {
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        )
            return;
        event.preventDefault();
        view.removeEventListener('load', observeAfterLoad);
        observe();
        // A direct play action is already a visible visitor interaction. Fresh
        // native bounds take over immediately without delaying that gesture.
        visible = true;
        fresh = true;
        wantsPlayback = true;
        start(true);
    };
    function dispose() {
        if (disposed) return;
        disposed = true;
        wantsPlayback = false;
        pendingRetry = null;
        view.removeEventListener('load', observeAfterLoad);
        observer?.disconnect();
        removalObserver?.disconnect();
        play.removeEventListener('click', onClick);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('ended', onEnded);
        pausePlayback();
    }
    play.addEventListener('click', onClick);
    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    removalObserver = new view.MutationObserver(() => {
        if (!root.isConnected) dispose();
    });
    removalObserver.observe(document.documentElement, { childList: true, subtree: true });
    if (autoplay) {
        if (document.readyState === 'complete') observeAfterLoad();
        else view.addEventListener('load', observeAfterLoad, { once: true });
    }
}

/** Hydrate on activation, or after page load and visibility for configured autoplay. */
export function initContentVideos(root = document) {
    if (root.matches?.('[data-content-video]')) bindPlayer(root);
    root.querySelectorAll?.('[data-content-video]').forEach(bindPlayer);
    initVideoVariants(root);
}
